import { useEffect, useMemo, useRef, useState } from 'react';
import { Api } from '../application/panelApi';
import { ICON } from '../components/Icons';
import { confirmDialog, modal } from '../components/ModalHost';
import { toast } from '../components/ToastHost';
import { createFormStore, type FormStore } from '../lib/formStore';
import { errText, fmtDate, nf, plural } from '../lib/format';
import { useApp } from '../store/AppContext';

const ROTATION_HINT =
  'IP меняется при каждом соединении, и сайты видят разные страны в одной сессии. Включите Sticky-сессии в кабинете провайдера и загрузите список заново.';

type ProxyPool = {
  id: string;
  tag: string;
  protocol: string;
  access: string;
  rotation?: string;
  addressCount: number;
  updatedAt: string;
};

type ProxyAddress = {
  index: number;
  protocol: string;
  host: string;
  port: number | string;
  hasAuth?: boolean;
  success?: number;
  failed?: number;
};

type ParseResult = {
  total?: number;
  errors?: string[];
  sample?: Array<{ protocol: string; host: string; port: number | string }>;
};

type UploadResult = {
  tag: string;
  added: number;
  skipped?: number;
  rotation?: string;
  errors?: string[];
};

type AuditFn = (act: string, obj?: string) => void;
type RefreshFn = () => Promise<void>;



type EditPoolForm = { tag: string; protocol: string; open: boolean; busy: boolean };

function EditPoolBody({ store }: { store: FormStore<EditPoolForm> }) {
  const f = store.useValue();
  return (
    <>
      <div className="field-row">
        <label htmlFor="ePoolTag">Тег пула</label>
        <input
          type="text"
          id="ePoolTag"
          value={f.tag}
          spellCheck={false}
          onChange={(e) => store.set({ tag: e.target.value })}
        />
      </div>
      <div className="field-row">
        <label htmlFor="ePoolProto">Протокол</label>
        <select id="ePoolProto" value={f.protocol} onChange={(e) => store.set({ protocol: e.target.value })}>
          <option>http</option>
          <option>https</option>
          <option>socks4</option>
          <option>socks5</option>
        </select>
      </div>
      <div className="set-row">
        <div className="txt">
          <b>Открытый доступ</b>
          <small>Если включено, пул смогут использовать другие пользователи вашего аккаунта.</small>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            id="ePoolOpen"
            checked={f.open}
            onChange={(e) => store.set({ open: e.target.checked })}
          />
          <i></i>
        </label>
      </div>
    </>
  );
}

function EditPoolFoot({
  store,
  pool,
  close,
  audit,
  refresh,
}: {
  store: FormStore<EditPoolForm>;
  pool: ProxyPool;
  close: () => void;
  audit: AuditFn;
  refresh: RefreshFn;
}) {
  const f = store.useValue();
  const save = async () => {
    const tag = store.get().tag.trim().toUpperCase();
    if (!tag) {
      toast('Укажите тег пула, например RU-RESIDENT-4G. По нему задачи находят прокси.', 'err');
      return;
    }
    store.set({ busy: true });
    try {
      const cur = store.get();
      await Api.proxyPools.update(pool.id, {
        tag,
        protocol: cur.protocol,
        access: cur.open ? 'open' : 'closed',
      });
      close();
      audit('Изменил пул прокси', tag);
      await refresh();
      toast(`Пул прокси ${tag} сохранён.`, 'ok');
    } catch (err) {
      store.set({ busy: false });
      toast(errText(err), 'err');
    }
  };
  return (
    <>
      <button type="button" className="btn" data-close>
        Отмена
      </button>
      <button type="button" className="btn btn-primary" id="ePoolSave" disabled={f.busy} onClick={() => void save()}>
        Сохранить
      </button>
    </>
  );
}

function openEditPool(pool: ProxyPool, audit: AuditFn, refresh: RefreshFn): void {
  const store = createFormStore<EditPoolForm>({
    tag: pool.tag,
    protocol: pool.protocol,
    open: pool.access === 'open',
    busy: false,
  });
  const holder: { close: () => void } = { close: () => undefined };
  holder.close = modal(
    `Изменить пул · ${pool.tag}`,
    <EditPoolBody store={store} />,
    <EditPoolFoot store={store} pool={pool} close={() => holder.close()} audit={audit} refresh={refresh} />,
    true,
  );
}



type UploadForm = {
  text: string;
  fileTexts: string[];
  fileCount: number;
  protocol: string;
  tag: string;
  open: boolean;
  replace: boolean;
  strict: boolean;
  preview: ParseResult | null;
  busy: boolean;
};

function collectContent(f: UploadForm): string {
  return [...f.fileTexts, f.text.trim()].filter(Boolean).join('\n');
}

function UploadBody({ store }: { store: FormStore<UploadForm> }) {
  const f = store.useValue();
  const errs = f.preview?.errors?.length || 0;
  return (
    <>
      <label className="file">
        <input
          type="file"
          accept=".txt"
          id="pFile"
          multiple
          onChange={(ev) => {
            const files = Array.from(ev.target.files ?? []);
            store.set({ fileCount: files.length });
            void Promise.all(files.map((file) => file.text())).then((texts) => store.set({ fileTexts: texts }));
          }}
        />
        <span className="btn btn-sm" style={{ pointerEvents: 'none' }}>
          Выбрать .txt
        </span>
        <span id="pFileName">{f.fileCount ? `${f.fileCount} файл(ов) выбрано` : 'Файлы не выбраны'}</span>
      </label>
      <div className="field-row">
        <label htmlFor="pList">Список прокси (файл и/или вставка)</label>
        <textarea
          id="pList"
          rows={7}
          spellCheck={false}
          placeholder={
            'Вставьте прокси, по одному на строку. Например:\nsocks5://user:pass@198.51.100.7:4001\nhttp://198.51.100.8:8080'
          }
          value={f.text}
          onChange={(e) => store.set({ text: e.target.value })}
        />
      </div>
      <div className="field-row">
        <label htmlFor="pProto">Протокол по умолчанию</label>
        <select id="pProto" value={f.protocol} onChange={(e) => store.set({ protocol: e.target.value })}>
          <option value="auto">авто</option>
          <option>http</option>
          <option>https</option>
          <option>socks4</option>
          <option>socks5</option>
        </select>
      </div>
      <div className="field-row">
        <label htmlFor="pTag">Тег пула</label>
        <input
          type="text"
          id="pTag"
          placeholder="Например: RU-RESIDENT-4G"
          spellCheck={false}
          value={f.tag}
          onChange={(e) => store.set({ tag: e.target.value })}
        />
      </div>
      <div className="set-row">
        <div className="txt">
          <b>Открытый доступ</b>
          <small>Если включено, пул смогут использовать другие пользователи вашего аккаунта.</small>
        </div>
        <label className="switch">
          <input type="checkbox" id="pOpen" checked={f.open} onChange={(e) => store.set({ open: e.target.checked })} />
          <i></i>
        </label>
      </div>
      <div className="set-row">
        <div className="txt">
          <b>Заменить существующие адреса</b>
          <small>Перед загрузкой удалить ранее загруженные адреса, чтобы остались только новые.</small>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            id="pReplace"
            checked={f.replace}
            onChange={(e) => store.set({ replace: e.target.checked })}
          />
          <i></i>
        </label>
      </div>
      <div className="set-row">
        <div className="txt">
          <b>Строгая проверка</b>
          <small>Если хоть одна строка с ошибкой, ничего не загружать. Выключите, чтобы пропускать ошибочные строки.</small>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            id="pStrict"
            checked={f.strict}
            onChange={(e) => store.set({ strict: e.target.checked })}
          />
          <i></i>
        </label>
      </div>
      <div className="imp" id="pPreview">
        {f.preview ? (
          <>
            <div className="imp-row ok">
              <b>{nf.format(f.preview.total || 0)}</b> валидных строк
            </div>
            <div className={`imp-row${errs ? ' err' : ''}`}>
              <b>{nf.format(errs)}</b> с ошибками
            </div>
            {f.preview.sample?.length ? (
              <div className="hint">
                Так будут прочитаны первые строки: {f.preview.sample.map((x) => `${x.protocol}://${x.host}:${x.port}`).join(', ')}
              </div>
            ) : null}
            {errs ? (
              <div className="hint" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                {(f.preview.errors ?? []).slice(0, 5).join('\n')}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}

function UploadFoot({
  store,
  close,
  audit,
  refresh,
}: {
  store: FormStore<UploadForm>;
  close: () => void;
  audit: AuditFn;
  refresh: RefreshFn;
}) {
  const f = store.useValue();

  const parse = async () => {
    const cur = store.get();
    const content = collectContent(cur);
    if (!content.trim()) {
      toast('Список пуст. Выберите .txt-файл или вставьте прокси в поле, по одному на строку.', 'err');
      return;
    }
    try {
      const r = (await Api.proxies.parse(content, cur.protocol)) as ParseResult;
      store.set({ preview: r });
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const upload = async () => {
    const cur = store.get();
    const content = collectContent(cur);
    if (!content.trim()) {
      toast('Список пуст. Выберите .txt-файл или вставьте прокси в поле, по одному на строку.', 'err');
      return;
    }
    const tag = (cur.tag.trim() || 'DEFAULT').toUpperCase();
    store.set({ busy: true });
    try {
      const r = (await Api.proxyPools.upload({
        content,
        tag,
        protocol: cur.protocol,
        access: cur.open ? 'open' : 'closed',
        replace: cur.replace,
        strict: cur.strict,
        dedup: true,
      })) as UploadResult;
      close();
      await refresh();
      audit('Загрузил прокси', `${r.tag} · ${r.added} адресов`);
      const skipped = r.skipped ? `, пропущено: ${r.skipped}` : '';
      toast(`Пул ${r.tag}: загружено адресов: ${nf.format(r.added)}${skipped}.`, 'ok');
      if (r.rotation === 'per-connection') toast(`Пул ${r.tag}: ${ROTATION_HINT}`, 'err');
      if (r.errors?.length)
        toast(
          `Строк с ошибками: ${r.errors.length}, они не загружены. Сверьте их с форматом в окне загрузки и добавьте снова.`,
          'err',
        );
    } catch (err) {
      store.set({ busy: false });
      toast(errText(err), 'err');
    }
  };

  return (
    <>
      <button type="button" className="btn" id="pParse" disabled={f.busy} onClick={() => void parse()}>
        Предпросмотр
      </button>
      <button type="button" className="btn" data-close>
        Отмена
      </button>
      <button type="button" className="btn btn-primary" id="pGo" disabled={f.busy} onClick={() => void upload()}>
        Загрузить
      </button>
    </>
  );
}

function openUpload(audit: AuditFn, refresh: RefreshFn): void {
  const store = createFormStore<UploadForm>({
    text: '',
    fileTexts: [],
    fileCount: 0,
    protocol: 'auto',
    tag: '',
    open: true,
    replace: false,
    strict: true,
    preview: null,
    busy: false,
  });
  const holder: { close: () => void } = { close: () => undefined };
  holder.close = modal(
    'Загрузить прокси',
    <UploadBody store={store} />,
    <UploadFoot store={store} close={() => holder.close()} audit={audit} refresh={refresh} />,
    true,
  );
}



type AddrForm = { query: string; onlyFail: boolean; selected: number[] };

function visibleAddrs(all: ProxyAddress[], f: AddrForm): ProxyAddress[] {
  const query = f.query.trim().toLowerCase();
  return all.filter(
    (p) =>
      (!query || `${p.host}:${p.port}`.toLowerCase().includes(query)) && (!f.onlyFail || (p.failed || 0) > 0),
  );
}

function AddrBody({ all, store }: { all: ProxyAddress[]; store: FormStore<AddrForm> }) {
  const f = store.useValue();
  const list = useMemo(() => visibleAddrs(all, f), [all, f]);
  const selected = useMemo(() => new Set(f.selected), [f.selected]);
  const allOn = list.length > 0 && list.every((p) => selected.has(p.index));

  const toggle = (index: number, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(index);
    else next.delete(index);
    store.set({ selected: [...next] });
  };

  return (
    <>
      <div className="field-row">
        <input
          type="text"
          id="aFilter"
          placeholder="Поиск по хосту или порту"
          spellCheck={false}
          value={f.query}
          onChange={(e) => store.set({ query: e.target.value, selected: [] })}
        />
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 8px' }}>
        <input
          type="checkbox"
          id="aOnlyFail"
          checked={f.onlyFail}
          onChange={(e) => store.set({ onlyFail: e.target.checked, selected: [] })}
        />{' '}
        только с ошибками
      </label>
      <div style={{ maxHeight: '52vh', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input
                  type="checkbox"
                  id="aAll"
                  aria-label="Выбрать все"
                  checked={allOn}
                  onChange={(e) => store.set({ selected: e.target.checked ? list.map((p) => p.index) : [] })}
                />
              </th>
              <th style={{ width: 48, textAlign: 'right' }}>#</th>
              <th style={{ textAlign: 'left' }}>Адрес</th>
              <th style={{ textAlign: 'right' }}>ok / fail</th>
            </tr>
          </thead>
          <tbody id="aBody">
            {list.map((p) => (
              <tr key={p.index} data-i={p.index}>
                <td>
                  <input
                    type="checkbox"
                    className="asel"
                    aria-label="Выбрать адрес"
                    checked={selected.has(p.index)}
                    onChange={(e) => toggle(p.index, e.target.checked)}
                  />
                </td>
                <td className="num" style={{ color: 'var(--text-3)' }}>
                  {p.index + 1}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {p.protocol}://{p.host}:{p.port}
                  {p.hasAuth ? <span style={{ color: 'var(--text-3)' }}> (auth)</span> : null}
                </td>
                <td className="num">
                  <span style={{ color: 'var(--ok)' }}>{p.success || 0}</span>{' '}
                  <span style={{ color: 'var(--border-strong)' }}>/</span>{' '}
                  <span style={{ color: p.failed ? 'var(--err)' : 'var(--text-3)' }}>{p.failed || 0}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="hint" id="aInfo" style={{ marginTop: 8 }}>
        {list.length === all.length && !f.query.trim() && !f.onlyFail
          ? `Всего адресов: ${nf.format(all.length)}.`
          : `Показано адресов: ${nf.format(list.length)} из ${nf.format(all.length)}.`}
      </div>
    </>
  );
}

function AddrFoot({
  all,
  store,
  close,
  audit,
  refresh,
}: {
  all: ProxyAddress[];
  store: FormStore<AddrForm>;
  close: () => void;
  audit: AuditFn;
  refresh: RefreshFn;
}) {
  const onDelete = () => {
    const f = store.get();
    const visible = new Set(visibleAddrs(all, f).map((p) => p.index));
    const indexes = f.selected.filter((i) => visible.has(i)).sort((a, b) => a - b);
    if (!indexes.length) {
      toast('Отметьте галочками адреса, которые нужно удалить.', 'err');
      return;
    }
    confirmDialog(
      'Удалить адреса?',
      <>
        Будет удалено <b>{nf.format(indexes.length)}</b> {plural(indexes.length, 'адрес', 'адреса', 'адресов')}.
        Вернуть их можно будет только повторной загрузкой.
      </>,
      async () => {
        try {
          const r = (await Api.proxies.remove(indexes)) as { removed: number };
          audit('Удалил адреса прокси', `${r.removed} шт.`);
          close();
          await refresh();
          toast(`Удалено адресов: ${nf.format(r.removed)}.`, 'ok');
        } catch (err) {
          toast(errText(err), 'err');
        }
      },
    );
  };
  return (
    <>
      <button type="button" className="btn btn-danger" id="aDel" onClick={onDelete}>
        Удалить выбранные
      </button>
      <button type="button" className="btn" data-close>
        Закрыть
      </button>
    </>
  );
}

async function openAddrViewer(audit: AuditFn, refresh: RefreshFn): Promise<void> {
  let all: ProxyAddress[] = [];
  try {
    const data = (await Api.proxies.list()) as { proxies?: ProxyAddress[] };
    all = data.proxies || [];
  } catch (err) {
    toast(errText(err), 'err');
    return;
  }
  if (!all.length) {
    toast('Адресов пока нет. Нажмите «Загрузить прокси», чтобы добавить их.', 'err');
    return;
  }
  const store = createFormStore<AddrForm>({ query: '', onlyFail: false, selected: [] });
  const holder: { close: () => void } = { close: () => undefined };
  holder.close = modal(
    'Адреса прокси',
    <AddrBody all={all} store={store} />,
    <AddrFoot all={all} store={store} close={() => holder.close()} audit={audit} refresh={refresh} />,
    true,
  );
}



export function ProxyPage() {
  const { proxyPools, proxyMeta, refreshProxy, audit } = useApp();
  const pools = proxyPools as ProxyPool[];

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const allRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void refreshProxy().catch((err: unknown) => toast(errText(err), 'err'));
  }, [refreshProxy]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return pools.filter((p) => !q || p.tag.toLowerCase().includes(q));
  }, [pools, filter]);

  const allChecked = rows.length > 0 && rows.every((p) => selected.has(p.id));
  const someChecked = rows.some((p) => selected.has(p.id));

  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = !allChecked && someChecked;
  }, [allChecked, someChecked]);

  const reload: RefreshFn = refreshProxy;

  const toggleOne = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const toggleAll = (on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of rows) {
        if (on) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });

  const onDelete = (p: ProxyPool) => {
    confirmDialog(
      'Удалить пул?',
      <>
        Пул прокси <b>{p.tag}</b> ({nf.format(p.addressCount)} адресов) будет удалён. Задачи с этим тегом останутся
        без прокси.
      </>,
      async () => {
        try {
          await Api.proxyPools.remove(p.id);
          setSelected((prev) => {
            const next = new Set(prev);
            next.delete(p.id);
            return next;
          });
          audit('Удалил пул прокси', p.tag);
          await reload();
          toast(`Пул прокси ${p.tag} удалён.`, 'err');
        } catch (err) {
          toast(errText(err), 'err');
        }
      },
    );
  };

  const onWipe = () => {
    if (!pools.length) return;
    const count = pools.length;
    confirmDialog(
      'Удалить все пулы прокси?',
      `Будет удалено ${count} ${plural(count, 'пул', 'пула', 'пулов')} прокси со всеми адресами. Задачи останутся без прокси и остановятся, пока вы не загрузите новые.`,
      async () => {
        try {
          await Api.proxyPools.clear();
          setSelected(new Set());
          audit('Удалил все пулы прокси', `${count} шт.`);
          await reload();
          toast('Все пулы прокси удалены. Загрузите новые, чтобы задачи снова заработали.', 'err');
        } catch (err) {
          toast(errText(err), 'err');
        }
      },
      { word: 'УДАЛИТЬ', yes: 'Удалить всё' },
    );
  };

  const last = pools.length
    ? fmtDate([...pools].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0]?.updatedAt)
    : '—';

  return (
    <section id="page-proxy">
      <div className="page-head">
        <div>
          <h1>Прокси</h1>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-primary" id="uploadProxyBtn" onClick={() => openUpload(audit, reload)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Загрузить прокси
          </button>
          <button type="button" className="btn" id="viewAddrBtn" onClick={() => void openAddrViewer(audit, reload)}>
            Адреса
          </button>
          <button type="button" className="btn btn-danger" id="wipeProxyBtn" onClick={onWipe}>
            Удалить все
          </button>
        </div>
      </div>

      <div className="panel proxy-panel">
        <div className="panel-head">
          <h2>Пулы прокси</h2>
          <span className="count" id="pxCount">
            {rows.length} из {pools.length}
          </span>
          <div className="tools">
            <input
              type="text"
              id="pxFilter"
              placeholder="Поиск по тегу пула"
              spellCheck={false}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="proxy-summary" aria-label="Сводка по прокси">
          <span>Адресов <b id="pxTotal">{nf.format(proxyMeta.totalAddresses)}</b></span>
          <span>Пулов <b id="pxGroups">{pools.length}</b></span>
          <span>Открыто <b id="pxOpen">{proxyMeta.openPools}</b></span>
          <span>Последняя загрузка <b id="pxLast">{last}</b></span>
        </div>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: 38 }}>
                  <input
                    type="checkbox"
                    id="pxAll"
                    aria-label="Выбрать все"
                    ref={allRef}
                    checked={allChecked}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th>Тег пула</th>
                <th>Протокол</th>
                <th>Доступ</th>
                <th style={{ textAlign: 'right' }}>Адресов</th>
                <th>Обновлён</th>
                <th style={{ textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody id="pxBody">
              {rows.length ? (
                rows.map((p) => (
                  <tr
                    key={p.id}
                    data-id={p.id}
                    style={{ cursor: 'pointer' }}
                    title="Нажмите, чтобы посмотреть адреса прокси и статистику успешных и неудачных подключений."
                    onClick={() => void openAddrViewer(audit, reload)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="psel"
                        aria-label="Выбрать пул"
                        checked={selected.has(p.id)}
                        onChange={(e) => toggleOne(p.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <span className="tag">{p.tag}</span>
                    </td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {p.protocol}
                      {p.rotation === 'per-connection' ? (
                        <>
                          {' '}
                          <span className="pill pause" title={ROTATION_HINT}>
                            <span className="led"></span>IP на каждое соединение
                          </span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <span className={`pill ${p.access === 'open' ? 'run' : 'stop'}`}>
                        <span className="led"></span>
                        {p.access === 'open' ? 'Открыт' : 'Закрыт'}
                      </span>
                    </td>
                    <td className="num">
                      <b>{nf.format(p.addressCount)}</b>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                      {fmtDate(p.updatedAt)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="icon-btn"
                          data-act="edit"
                          title="Изменить тег, протокол и доступ пула."
                          onClick={() => openEditPool(p, audit, reload)}
                        >
                          {ICON.edit}
                        </button>
                        <button
                          type="button"
                          className="icon-btn danger"
                          data-act="del"
                          title="Удалить пул вместе со всеми его адресами."
                          onClick={() => onDelete(p)}
                        >
                          {ICON.del}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className="empty">Пулов не найдено</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
